import { Typography } from '@euler/app/components/typography';
import { canManageApdmImports } from '@euler/functions/apdmAccess';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useUserProfileInfo } from '@euler/functions/user/useUserProfileInfo';
import { ApdmBuildCardKind, ApdmBuildStep } from '@euler/model/apdm';
import { MessageSquareText, RefreshCw } from 'lucide-react-native';
import { memo, useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { useAiChatActions, useAiPlanStepOpen } from '../chat/AiChatActions';
import { CardLoading, CardNotice, ChatCard } from '../components/ChatCard';
import { PrimaryButton, SecondaryButton } from '../components/PrimaryButton';
import { aiColors } from '../theme';
import { isAiMessageId, usePlanCard } from './usePlanCard';

/**
 * The texts drafted for the plan, sent to customers in turn: edit them in
 * place and accept, or ask for another draft (spec §11.4.2).
 */
export const PlanMessagesCard = memo(
  ({ messageId, streaming }: { messageId?: string; streaming?: boolean }) => {
    const profile = useUserProfileInfo();
    if (streaming || !isAiMessageId(messageId)) return null;
    if (!canManageApdmImports(profile ?? null))
      return <CardNotice text="当前账号无法编辑活动文案。" />;
    return <MessagesBody key={messageId} messageId={messageId} />;
  },
);

function MessagesBody({ messageId }: { messageId: string }) {
  const { rpx } = useResponsiveContext();
  const chat = useAiChatActions();
  const open = useAiPlanStepOpen(ApdmBuildStep.Messages);
  const state = usePlanCard(messageId, ApdmBuildCardKind.Messages);
  const drafted =
    state.kind === 'ready' && state.view.card === ApdmBuildCardKind.Messages
      ? state.view.messages
      : null;
  const [texts, setTexts] = useState<string[]>([]);
  useEffect(() => {
    if (drafted) setTexts(drafted.variants.map(variant => variant.text));
  }, [drafted]);
  if (state.kind === 'loading')
    return (
      <ChatCard icon={MessageSquareText} title="消息文案">
        <CardLoading label="正在读取文案…" />
      </ChatCard>
    );
  if (!drafted)
    return (
      <ChatCard icon={MessageSquareText} title="消息文案">
        <CardNotice text="没有读取到这些文案，请稍后重新打开对话。" />
      </ChatCard>
    );
  const edited = texts.some(
    (text, index) => text !== drafted.variants[index]?.text,
  );
  const ready = texts.length >= 3 && texts.every(text => text.trim());
  return (
    <ChatCard
      icon={MessageSquareText}
      title={drafted.title}
      subtitle="每位客户收到其中一条，轮流发送"
    >
      {texts.map((text, index) => (
        <View key={index} style={{ gap: rpx(4) }}>
          <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
            第 {index + 1} 条
          </Typography.Text>
          <MessageInput
            text={text}
            editable={open}
            onChangeText={value =>
              setTexts(previous =>
                previous.map((item, at) => (at === index ? value : item)),
              )
            }
          />
        </View>
      ))}
      <Typography.Text size={rpx(11)} color={aiColors.mutedForeground}>
        花括号里的内容会换成每位客户自己的信息，例如 {'{plateNo}'} 是车牌号。
      </Typography.Text>
      {open ? (
        <>
          <PrimaryButton
            title={edited ? '用修改后的文案' : '都可以'}
            disabled={!ready}
            onPress={() =>
              chat.send(edited ? '用我修改后的文案' : '文案都可以', {
                answer: {
                  step: ApdmBuildStep.Messages,
                  variants: texts.map((value, index) => ({
                    text: value.trim(),
                    applicability:
                      drafted.variants[index]?.applicability ?? null,
                  })),
                },
              })
            }
          />
          <SecondaryButton
            title="再换一种写法"
            icon={RefreshCw}
            onPress={() => chat.send('再换一种写法')}
          />
        </>
      ) : null}
    </ChatCard>
  );
}

/** One message, as tall as its text: the whole draft reads at a glance. */
function MessageInput({
  text,
  editable,
  onChangeText,
}: {
  text: string;
  editable: boolean;
  onChangeText: (value: string) => void;
}) {
  const { rpx } = useResponsiveContext();
  const [height, setHeight] = useState<number | undefined>(undefined);
  return (
    <TextInput
      value={text}
      editable={editable}
      multiline
      scrollEnabled={false}
      onChangeText={onChangeText}
      // The web input does not grow with its text; native ones do. The
      // reported height includes the padding, so it is the box's own.
      onContentSizeChange={event => {
        const next = Math.ceil(event.nativeEvent.contentSize.height);
        setHeight(previous => (previous === next ? previous : next));
      }}
      style={{
        padding: rpx(10),
        borderRadius: rpx(10),
        borderWidth: 1,
        borderColor: aiColors.border,
        backgroundColor: editable ? aiColors.card : aiColors.muted,
        color: aiColors.foreground,
        fontSize: rpx(13),
        lineHeight: rpx(20),
        minHeight: height,
      }}
    />
  );
}
